import React, { useContext } from 'react';

import { AsaphButton } from '@/components/shared/Button';
import { FormInput } from '@/components/shared/Inputs';
import { classNames } from '@/helpers/classNames';
import { useErrorHandler } from '@/hooks/useErrorHandler';
import { useAnalyticsEvent } from '@/providers/AnalyticsProvider';
import { useAuth } from '@/providers/AuthProvider';
import { Environments, useEnvironment } from '@/providers/EnvironmentProvider';
import { FormHelperText } from '@material-ui/core';
import axios, { AxiosError } from 'axios';
import { Formik } from 'formik';
import * as Yup from 'yup';

import styles from '../../../Login/LoginPage.module.scss';
import {
	CopilotContext,
	CopilotState,
	sendMessageToPco,
} from '../../CopilotPage';
import copilotStyles from '../../CopilotPage.module.scss';
import { AuthErrorResponse } from '../CopilotCreateUser';

const signUpValidationSchema = Yup.object().shape({
	code: Yup.string().required('Please enter your code'),
});

const VerifyForm: React.FC<{
	email: string;
	onChange: (email: string | null) => void;
}> = ({ email, onChange }) => {
	const {
		api: { url },
		environment,
	} = useEnvironment();
	const { authData, setToken, setState, token } = useContext(CopilotContext);
	const { handleError } = useErrorHandler();

	return (
		<Formik
			key="asaphSignUn"
			initialValues={{
				code: '',
			}}
			validationSchema={signUpValidationSchema}
			onSubmit={async (values, formikHelpers) => {
				if (values.code.toString().length < 4) {
					return formikHelpers.setFieldError(
						'code',
						'Code is invalid',
					);
				}
				try {
					if (!authData?.id) {
						throw new Error('No auth data');
					}

					await axios.post(`${url}/copilot/link-user/confirm`, {
						email,
						code: values.code.toString(),
						pcoId: authData.id,
					});

					// Retry logging in the user
					setState(CopilotState.AuthUser);
				} catch (err) {
					if (err instanceof AxiosError) {
						const code = (err.response?.data as AuthErrorResponse)
							?.code;

						if (code === 'InvalidCode') {
							formikHelpers.setErrors({
								code: 'Invalid code',
							});
							return;
						}
						if (code === 'MembershipNotFound') {
							handleError({
								error: err,
								errorCode: 'MembershipNotFound',
								friendlyMessage:
									'Your user was found but has no memberships associated.',
							});
							return;
						}
					}
					setState(CopilotState.Error);
					handleError({
						error: err,
						errorCode: 'CopilotLinkError',
						friendlyMessage:
							'Something went wrong. Please try again later.',
					});
				}
			}}
		>
			{({
				handleSubmit,
				handleChange,
				values,
				isSubmitting,
				setFieldValue,
				touched,
				errors,
			}) => (
				<form
					onSubmit={handleSubmit}
					id="asaphSignIn"
					style={{ height: '100%' }}
				>
					<div className={copilotStyles['flex-between']}>
						<div className={copilotStyles['form-container']}>
							<div className={copilotStyles['blurb']}>
								One-time code sent to{' '}
								<strong style={{ color: 'black' }}>
									{email}
								</strong>
								.
							</div>
							<div className="vsb-20" />

							<div className="text-center">
								<FormInput
									id="code"
									name="code"
									type="number"
									placeholder="1234"
									onChange={handleChange}
									value={values.code}
									className={classNames([
										styles['code-input'],
										copilotStyles['code-input'],
									])}
								/>
							</div>
							<div className="vsb-20">
								{!!errors.code && touched.code && (
									<FormHelperText
										error={true}
										classes={{
											root: 'authentication-error-text',
										}}
									>
										{errors.code}
									</FormHelperText>
								)}
							</div>
						</div>

						<div className={copilotStyles['accept-row']}>
							<AsaphButton
								label="Back"
								onClick={() => onChange(null)}
								color="grey"
							/>
							<AsaphButton
								label="Continue"
								type="submit"
								color="blue"
								fullWidth
								disabled={isSubmitting}
							/>
						</div>
					</div>
				</form>
			)}
		</Formik>
	);
};

export default VerifyForm;
